import { useEffect, useRef, useState } from 'react';
import {
  useProgress,
  addCustomPhrase,
  updateCustomPhrase,
  removeCustomPhrase,
} from '../store/progress.js';
import { Icons } from './Icons.jsx';
import { ChunkyButton, GhostButton } from './Buttons.jsx';
import TopStatsBar from './TopStatsBar.jsx';
import BottomTabs from './BottomTabs.jsx';
import { MajaTip } from './Mascot.jsx';
import { speak } from '../utils/speech.js';
import { BALL } from '../theme.js';

const T = BALL;

function PhraseRow({ phrase, lang, onEdit }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 14px', background: T.bgAlt,
      border: `2px solid ${T.hairline}`, borderRadius: 14,
      boxShadow: `0 3px 0 ${T.hairline}`,
    }}>
      <button
        type="button"
        onClick={() => speak(phrase.hr)}
        aria-label={lang === 'en' ? 'Play audio' : 'Spela upp'}
        style={{
          appearance: 'none', border: 'none', cursor: 'pointer',
          width: 40, height: 40, borderRadius: '50%',
          background: T.blue, color: '#fff', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 3px 0 ${T.blueDeep}`,
        }}
      >
        {Icons.speaker('#fff')}
      </button>
      <button
        type="button"
        onClick={() => onEdit(phrase)}
        style={{
          appearance: 'none', background: 'none', border: 'none', cursor: 'pointer',
          flex: 1, textAlign: 'left', padding: 0, fontFamily: 'inherit', minWidth: 0,
        }}
      >
        <div style={{
          fontSize: 16, fontWeight: 900, color: T.primary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {phrase.hr}
        </div>
        <div style={{
          fontSize: 13, color: T.inkSoft, marginTop: 2, fontWeight: 600,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {phrase.meaning}
        </div>
      </button>
      <div style={{ color: T.inkSoft, opacity: 0.6, fontSize: 18 }}>›</div>
    </div>
  );
}

function PhraseEditor({ initial, lang, onClose }) {
  const isEdit = Boolean(initial?.id);
  const [hr, setHr] = useState(initial?.hr || '');
  const [meaning, setMeaning] = useState(initial?.meaning || '');
  const hrRef = useRef(null);

  useEffect(() => { hrRef.current?.focus(); }, []);

  function handleSave() {
    if (!hr.trim() || !meaning.trim()) return;
    if (isEdit) updateCustomPhrase(initial.id, { hr, meaning });
    else addCustomPhrase({ hr, meaning });
    onClose();
  }

  function handleDelete() {
    if (!isEdit) return;
    const confirmText = lang === 'en'
      ? `Delete "${initial.hr}"?`
      : `Ta bort "${initial.hr}"?`;
    if (confirm(confirmText)) {
      removeCustomPhrase(initial.id);
      onClose();
    }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 60,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: T.bgAlt, width: '100%', maxWidth: 480,
        borderRadius: '20px 20px 0 0',
        maxHeight: '85vh', overflowY: 'auto',
        padding: `12px 18px calc(env(safe-area-inset-bottom) + 18px)`,
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0 0 4px' }}>
          <div style={{ width: 40, height: 4, background: T.hairline, borderRadius: 2 }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: T.ink }}>
            {isEdit
              ? (lang === 'en' ? 'Edit phrase' : 'Redigera fras')
              : (lang === 'en' ? 'New phrase' : 'Ny fras')}
          </h2>
          <button type="button" onClick={onClose} style={{
            appearance: 'none', background: 'none', border: 'none', cursor: 'pointer',
          }}>
            {Icons.close(T.inkSoft)}
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{
            fontSize: 11, fontWeight: 900, color: T.inkSoft,
            letterSpacing: '0.12em', textTransform: 'uppercase',
          }}>
            {lang === 'en' ? 'Croatian' : 'Kroatiska'}
          </label>
          <input
            ref={hrRef}
            type="text"
            value={hr}
            onChange={(e) => setHr(e.target.value)}
            placeholder={lang === 'en' ? 'e.g. Gdje je kupaonica?' : 't.ex. Gdje je kupaonica?'}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            style={{
              border: `2px solid ${T.hairline}`, borderRadius: 12,
              padding: '12px 14px', fontSize: 17, fontWeight: 700,
              fontFamily: 'inherit', color: T.primary, background: T.bg,
              outline: 'none',
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{
            fontSize: 11, fontWeight: 900, color: T.inkSoft,
            letterSpacing: '0.12em', textTransform: 'uppercase',
          }}>
            {lang === 'en' ? 'Translation' : 'Översättning'}
          </label>
          <input
            type="text"
            value={meaning}
            onChange={(e) => setMeaning(e.target.value)}
            placeholder={lang === 'en' ? 'e.g. Where is the bathroom?' : 't.ex. Var är toaletten?'}
            autoComplete="off"
            autoCorrect="off"
            style={{
              border: `2px solid ${T.hairline}`, borderRadius: 12,
              padding: '12px 14px', fontSize: 17, fontWeight: 700,
              fontFamily: 'inherit', color: T.ink, background: T.bg,
              outline: 'none',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          {isEdit && (
            <GhostButton
              size="md"
              onClick={handleDelete}
              style={{ color: T.red, borderColor: '#FFB3B3', boxShadow: '0 3px 0 #FFB3B3' }}
            >
              {lang === 'en' ? 'Delete' : 'Ta bort'}
            </GhostButton>
          )}
          <div style={{ flex: 1 }} />
          <GhostButton size="md" onClick={onClose}>
            {lang === 'en' ? 'Cancel' : 'Avbryt'}
          </GhostButton>
          <ChunkyButton
            color="primary"
            size="md"
            disabled={!hr.trim() || !meaning.trim()}
            onClick={handleSave}
          >
            {lang === 'en' ? 'Save' : 'Spara'}
          </ChunkyButton>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ lang, onAdd }) {
  return (
    <div style={{
      padding: '40px 24px', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 14, color: T.inkSoft, textAlign: 'center',
    }}>
      <div style={{ fontSize: 56 }}>📝</div>
      <h3 style={{ fontSize: 20, fontWeight: 900, color: T.ink, margin: 0 }}>
        {lang === 'en' ? 'No phrases yet' : 'Inga fraser än'}
      </h3>
      <p style={{ maxWidth: 320, margin: 0, lineHeight: 1.4 }}>
        {lang === 'en'
          ? "Save sentences or words you actually want to learn — a phrase for the bakery, something to say to your Croatian relatives, anything."
          : 'Spara meningar och ord du faktiskt vill lära dig — en fras för bageriet, något att säga till dina kroatiska släktingar, vad som helst.'}
      </p>
      <ChunkyButton color="primary" onClick={onAdd}>
        {lang === 'en' ? '+ Add your first phrase' : '+ Lägg till din första fras'}
      </ChunkyButton>
    </div>
  );
}

export default function Phrases({ onStartLesson, onTabChange, onOpenSettings }) {
  const progress = useProgress();
  const lang = progress.sourceLang;
  const phrases = progress.customPhrases || [];
  const [editing, setEditing] = useState(null);

  function startPractice() {
    if (phrases.length === 0) return;
    const items = phrases.map((p) => ({
      hr: p.hr,
      sv: p.meaning,
      en: p.meaning,
    }));
    const skill = {
      id: 'custom-phrases',
      emoji: '📝',
      color: T.gold,
      level: 'custom',
      name: { sv: 'Mina fraser', en: 'My phrases' },
      blurb: { sv: '', en: '' },
      items,
    };
    const lesson = {
      id: 'custom-phrases-practice',
      index: 0,
      items,
      isReview: true,
    };
    onStartLesson(skill, lesson);
  }

  return (
    <div className="app-shell">
      <TopStatsBar
        streak={progress.streak}
        gems={progress.gems}
        theme={T}
        onSettings={onOpenSettings}
      />

      <div className="app-scroll">
        <div style={{ padding: '16px 16px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          }}>
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 900, color: T.ink, margin: 0 }}>
                {lang === 'en' ? 'My phrases' : 'Mina fraser'}
              </h2>
              <div style={{ fontSize: 13, color: T.inkSoft, marginTop: 2 }}>
                {phrases.length === 0
                  ? (lang === 'en' ? 'Build your own practice deck' : 'Bygg din egen övningslista')
                  : `${phrases.length} ${
                      phrases.length === 1
                        ? (lang === 'en' ? 'phrase' : 'fras')
                        : (lang === 'en' ? 'phrases' : 'fraser')
                    }`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEditing({ hr: '', meaning: '' })}
              aria-label={lang === 'en' ? 'Add phrase' : 'Lägg till fras'}
              style={{
                appearance: 'none', border: 'none', cursor: 'pointer',
                width: 48, height: 48, borderRadius: '50%',
                background: T.primary, color: '#fff', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 0 ${T.primaryDeep}`,
                fontSize: 28, fontWeight: 900, lineHeight: 1, paddingBottom: 4,
              }}
            >
              +
            </button>
          </div>
        </div>

        {phrases.length === 0 ? (
          <EmptyState lang={lang} onAdd={() => setEditing({ hr: '', meaning: '' })} />
        ) : (
          <>
            <div style={{ padding: '12px 16px 4px' }}>
              <MajaTip theme={T} mood="happy" label="MAJA KAŽE">
                {lang === 'en'
                  ? 'Tap the speaker to hear how it sounds. Tap a row to edit or delete.'
                  : 'Tryck på högtalaren för att höra. Tryck på raden för att redigera eller ta bort.'}
              </MajaTip>
            </div>

            <div style={{
              padding: '8px 16px 16px',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {phrases.map((p) => (
                <PhraseRow
                  key={p.id}
                  phrase={p}
                  lang={lang}
                  onEdit={(phrase) => setEditing(phrase)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {phrases.length > 0 && (
        <div style={{
          borderTop: `1px solid ${T.hairline}`, background: T.bgAlt,
          padding: '14px 16px',
          display: 'flex', justifyContent: 'center',
        }}>
          <ChunkyButton color="primary" onClick={startPractice} style={{ flex: 1, maxWidth: 360 }}>
            {lang === 'en' ? 'Practice now' : 'Öva nu'}
          </ChunkyButton>
        </div>
      )}

      <BottomTabs active="phrases" onSelect={onTabChange} theme={T} />

      {editing && (
        <PhraseEditor
          initial={editing}
          lang={lang}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
